'use client';

import { useNavigate } from 'react-router-dom';
import { DocumentTextIcon, PencilSquareIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface ContentPipelineProps {
    draftCount: number;
    scheduledCount: number;
    publishedCount: number;
}

export default function ContentPipeline({ draftCount, scheduledCount, publishedCount }: ContentPipelineProps) {
    const navigate = useNavigate();

    const handleStatusClick = (status: string) => {
        navigate(`/articles?status=${status}`);
    };

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-2">📊</span>
                Content Pipeline
            </h2>

            <div className="space-y-3">
                {/* Draft */}
                <button
                    onClick={() => handleStatusClick('draft')}
                    className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer group"
                >
                    <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                            <DocumentTextIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4 text-left">
                            <p className="text-sm font-medium text-gray-600">Draft</p>
                            <p className="text-xs text-gray-500">In progress</p>
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-blue-600 group-hover:scale-110 transition-transform">
                        {draftCount}
                    </div>
                </button>

                {/* Scheduled */}
                <button
                    onClick={() => handleStatusClick('scheduled')}
                    className="w-full flex items-center justify-between p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors cursor-pointer group"
                >
                    <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                            <PencilSquareIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4 text-left">
                            <p className="text-sm font-medium text-gray-600">Scheduled</p>
                            <p className="text-xs text-gray-500">Ready to publish</p>
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-yellow-600 group-hover:scale-110 transition-transform">
                        {scheduledCount}
                    </div>
                </button>

                {/* Published */}
                <button
                    onClick={() => handleStatusClick('published')}
                    className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors cursor-pointer group"
                >
                    <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                            <CheckCircleIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4 text-left">
                            <p className="text-sm font-medium text-gray-600">Published</p>
                            <p className="text-xs text-gray-500">Live content</p>
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-green-600 group-hover:scale-110 transition-transform">
                        {publishedCount}
                    </div>
                </button>
            </div>

            {/* Pipeline visualization */}
            <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="text-center flex-1">
                        <div className="text-2xl mb-1">✍️</div>
                        <div>Draft</div>
                    </div>
                    <div className="flex-shrink-0 mx-2">→</div>
                    <div className="text-center flex-1">
                        <div className="text-2xl mb-1">📅</div>
                        <div>Schedule</div>
                    </div>
                    <div className="flex-shrink-0 mx-2">→</div>
                    <div className="text-center flex-1">
                        <div className="text-2xl mb-1">🚀</div>
                        <div>Publish</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
